import React, { useState, useContext } from 'react';
import styled from 'styled-components';

import ButtonPanel from './ButtonPanel';
import { RoundButton } from './Buttons';
import ContentBox from './ContentBox';
import ChatWizardHeader from './ChatWizardHeader';
import { Input, CheckboxButton } from './Input';

import i18n from '../i18n';
import Layout from '../layout/Layout2020';
import ThemeContext from '../contexts/ThemeContext';

const StyledContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: ${({ padding }) => padding * 3}px;
`;

const EmailRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 15px;
`;

const StyledInput = styled(Input)`
  display: block;
  font-size: 16px;
  flex: 1;
  height: 48px;
  min-height: 48px;
  padding: 10px 10px;
  background-color: #ffffff;
`;

const CheckboxRow = styled.div`
  width: 100%;
  margin-top: 20px;
`;

// The global stylesheet styles bare <label> text white (app.scss `label { color: #fff }`),
// so — like the other CheckboxButton call sites — wrap the label in a span with an explicit color.
const SummaryLabel = styled.span`
  margin-left: 8px;
  font-size: 16px;
  color: ${({ color }) => color};
`;

const StyledErrorMessage = styled.div`
  width: 100%;
  color: ${({ color }) => color};
  padding: 5px 0;
`;

const AllowedDomainsHint = styled(StyledErrorMessage)`
  font-size: 14px;
`;

// Free email input: any address with a local part, '@' and a dotted domain.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Keep in sync with the platform's cap (transcriptDelivery MAX_CUSTOM_SUBJECT_LENGTH).
const MAX_SUBJECT_LENGTH = 100;

const SendTranscriptEmailDialog = ({ defaultEmail, allowedDomains, sending, onSubmit, onCancel }) => {
  const { colors } = useContext(ThemeContext);

  const [email, setEmail] = useState(defaultEmail || '');
  const [subject, setSubject] = useState('');
  const [includeSummary, setIncludeSummary] = useState(false);
  const [error, setError] = useState('');

  // The organisation's email-sending whitelist from the user info; null means the backend
  // does not provide one (older platform), in which case only the format is validated.
  // The backend enforces the same rule authoritatively on send.
  const domains = Array.isArray(allowedDomains)
    ? allowedDomains.map((domain) => String(domain).trim().toLowerCase()).filter(Boolean)
    : null;

  // The user's own account email is always deliverable; any other recipient needs a
  // whitelisted domain (matched exactly on the part after the last '@').
  const isRecipientAllowed = (address) => {
    if (!EMAIL_PATTERN.test(address)) {
      return false;
    }
    if (!domains) {
      return true;
    }
    const normalized = address.toLowerCase();
    const ownEmail = (defaultEmail || '').trim().toLowerCase();
    if (ownEmail && normalized === ownEmail) {
      return true;
    }
    return domains.includes(normalized.slice(normalized.lastIndexOf('@') + 1));
  };

  const submit = () => {
    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setError('ai_email_invalid');
      return;
    }
    if (!isRecipientAllowed(trimmed)) {
      setError('ai_email_domain_not_allowed');
      return;
    }
    setError('');
    onSubmit(trimmed, includeSummary, subject.trim());
  };

  return (
    <StyledContainer padding={Layout.padding}>
      <ChatWizardHeader title={i18n('ai_send_email_title')} />
      <ContentBox minorTitle={i18n('ai_send_email_explanation')}>
        <EmailRow>
          <StyledInput
            type="email"
            placeholder={i18n('email')}
            value={email}
            onChange={setEmail}
            onFocus={() => setError('')}
            onNext={submit}
          />
        </EmailRow>
        {error ? (
          <StyledErrorMessage color={colors.error}>{i18n(error)}</StyledErrorMessage>
        ) : null}
        {domains ? (
          <AllowedDomainsHint color={colors.webDarkBlue}>
            {domains.length
              ? `${i18n('ai_allowed_email_domains')} ${domains.join(', ')}`
              : i18n('ai_own_email_only')}
          </AllowedDomainsHint>
        ) : null}
        <EmailRow>
          <StyledInput
            type="text"
            maxLength={MAX_SUBJECT_LENGTH}
            placeholder={i18n('ai_email_subject_placeholder')}
            value={subject}
            onChange={setSubject}
            onNext={submit}
          />
        </EmailRow>
        <CheckboxRow>
          <CheckboxButton
            checked={includeSummary}
            handleChange={() => setIncludeSummary((prev) => !prev)}
            size={20}
            lowerCase
          >
            <SummaryLabel color={colors.webDarkBlue}>{i18n('ai_include_summary')}</SummaryLabel>
          </CheckboxButton>
        </CheckboxRow>
      </ContentBox>
      <ButtonPanel>
        <RoundButton
          color={colors.tulkaMainColor}
          padding={Layout.buttonPadding}
          label={i18n('cancel')}
          onClick={onCancel}
        />
        <RoundButton
          darkButton
          disabled={sending || !isRecipientAllowed(email.trim())}
          color={colors.tulkaMainColor}
          padding={Layout.buttonPadding}
          label={sending ? i18n('please_wait') : i18n('proceed')}
          onClick={submit}
        />
      </ButtonPanel>
    </StyledContainer>
  );
};

export default SendTranscriptEmailDialog;
